import {
  filter,
  find,
  get,
  includes,
  isArray,
  map,
  size,
  trim,
  toLower,
} from 'lodash';
import encode from 'mout/queryString/encode';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import Loading from 'components/Loading';
import { PERMISSION_KEYS } from 'app/config/admin-dashboard-permissions';
import getHelpfulErrorMessage from 'utils/getHelpfulErrorMessage';
import {
  addPermissionUser,
  listPermissionKeys,
  listPermissionUsers,
  removePermissionUser,
  setUserPermission,
} from 'utils/admin-dashboard-permissions-api';
import api from 'api';

const toggleableKeysFromCatalog = (keys) => filter(
  keys,
  (item) => item.key !== PERMISSION_KEYS.ADMIN_DASHBOARD,
);

const Permissions = ({ classes, currentUser, loadCurrentUser }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [catalogKeys, setCatalogKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [busyUserId, setBusyUserId] = useState(null);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [userPendingRemoval, setUserPendingRemoval] = useState(null);

  const toggleableKeys = useMemo(
    () => toggleableKeysFromCatalog(catalogKeys),
    [catalogKeys],
  );

  const managePermissionsHolderCount = useMemo(
    () => size(filter(
      users,
      (user) => includes(user.permissions, PERMISSION_KEYS.MANAGE_PERMISSIONS),
    )),
    [users],
  );

  const loadLists = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [keysResponse, usersResponse] = await Promise.all([
        listPermissionKeys(),
        listPermissionUsers(),
      ]);
      const keys = keysResponse.keys || keysResponse;
      const grantedUsers = usersResponse.users || usersResponse;
      setCatalogKeys(isArray(keys) ? keys : []);
      setUsers(isArray(grantedUsers) ? grantedUsers : []);
    } catch (error) {
      setLoadError(getHelpfulErrorMessage(
        error,
        'Could not load permissions. This page needs the api-service permissions endpoints.',
      ));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const handleToggle = async (user, permissionKey, granted) => {
    setActionError(null);
    setBusyUserId(user.id);
    try {
      await setUserPermission(user.id, permissionKey, granted);
      await loadLists();
      if (user.id === currentUser.id) {
        loadCurrentUser();
      }
    } catch (error) {
      setActionError(getHelpfulErrorMessage(error));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRemove = async () => {
    if (!userPendingRemoval) {
      return;
    }
    setActionError(null);
    setBusyUserId(userPendingRemoval.id);
    try {
      await removePermissionUser(userPendingRemoval.id);
      setUserPendingRemoval(null);
      await loadLists();
      if (userPendingRemoval.id === currentUser.id) {
        loadCurrentUser();
      }
    } catch (error) {
      setActionError(getHelpfulErrorMessage(error));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleSearch = async () => {
    const query = trim(toLower(searchQuery));
    if (!query) {
      setSearchError('Enter an email to search.');
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    try {
      const queryString = encode({
        per_page: 10,
        page: 1,
      });
      const response = await api.post(`/users/filter${queryString}`, {
        conditions: [{
          field: 'email',
          operator: 'contains',
          value: query,
        }],
      });
      setSearchResults(response.results || []);
      if (!size(response.results)) {
        setSearchError('No users found with that email.');
      }
    } catch (error) {
      setSearchError(getHelpfulErrorMessage(error));
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddUser = async (user) => {
    setActionError(null);
    setBusyUserId(user.id);
    try {
      await addPermissionUser(user.id);
      setIsAddOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      setSearchError(null);
      await loadLists();
    } catch (error) {
      setActionError(getHelpfulErrorMessage(error));
    } finally {
      setBusyUserId(null);
    }
  };

  const closeAddDialog = () => {
    setIsAddOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
  };

  const isLastManagePermissionsHolder = (user) => (
    includes(user.permissions, PERMISSION_KEYS.MANAGE_PERMISSIONS)
    && managePermissionsHolderCount <= 1
  );

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="h6">
          Permissions
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsAddOpen(true)}
          disabled={isLoading}
        >
          Add user
        </Button>
      </div>

      <Typography variant="body2" color="textSecondary" className={classes.intro}>
        People on this list can use the admin dashboard. Extra switches control
        engineering support, Migrations, and who can edit this list.
      </Typography>

      {loadError && (
        <Alert severity="error" className={classes.message}>
          {loadError}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" className={classes.message}>
          {actionError}
        </Alert>
      )}

      <Card className={classes.card}>
        <CardContent>
          {isLoading ? (
            <Loading />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  {map(toggleableKeys, (item) => (
                    <TableCell key={item.key}>{item.label || item.key}</TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {map(users, (user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.first_name}
                      {' '}
                      {user.last_name}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    {map(toggleableKeys, (item) => {
                      const checked = includes(user.permissions, item.key);
                      const isManageKey = item.key === PERMISSION_KEYS.MANAGE_PERMISSIONS;
                      const disableUncheck = isManageKey
                        && checked
                        && isLastManagePermissionsHolder(user);
                      return (
                        <TableCell key={item.key}>
                          <Switch
                            color="primary"
                            checked={checked}
                            disabled={busyUserId === user.id || disableUncheck}
                            onChange={(event) => handleToggle(
                              user,
                              item.key,
                              event.target.checked,
                            )}
                            inputProps={{
                              'aria-label': item.label || item.key,
                            }}
                          />
                        </TableCell>
                      );
                    })}
                    <TableCell align="right">
                      <Button
                        color="secondary"
                        disabled={
                          busyUserId === user.id
                          || isLastManagePermissionsHolder(user)
                        }
                        onClick={() => setUserPendingRemoval(user)}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!size(users) && !loadError && (
                  <TableRow>
                    <TableCell colSpan={3 + size(toggleableKeys)}>
                      <Typography variant="body2" color="textSecondary">
                        No approved users yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isAddOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Add user
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Search by email. They must already have a Beacon account.
          </Typography>
          <TextField
            label="Email"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            variant="outlined"
            fullWidth
            className={classes.searchField}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          {searchError && (
            <Alert severity="warning" className={classes.message}>
              {searchError}
            </Alert>
          )}
          {isSearching && <Loading />}
          {map(searchResults, (result) => {
            const alreadyAdded = Boolean(find(users, ['id', result.id]));
            return (
              <Card key={result.id} variant="outlined" className={classes.searchResult}>
                <CardContent>
                  <Typography variant="body2">
                    {result.first_name}
                    {' '}
                    {result.last_name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {result.email}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.addResultButton}
                    disabled={alreadyAdded || busyUserId === result.id}
                    onClick={() => handleAddUser(result)}
                  >
                    {alreadyAdded ? 'Already on the list' : 'Add'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>
            Close
          </Button>
          <Button
            color="primary"
            onClick={handleSearch}
            disabled={isSearching}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(userPendingRemoval)}
        onClose={() => setUserPendingRemoval(null)}
      >
        <DialogTitle>
          Remove from admin dashboard
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {get(userPendingRemoval, 'email')}
            {' '}
            will no longer be able to use the admin dashboard.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserPendingRemoval(null)}>
            Cancel
          </Button>
          <Button
            color="secondary"
            onClick={handleRemove}
            disabled={busyUserId === get(userPendingRemoval, 'id')}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const styles = (theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  intro: {
    marginBottom: theme.spacing(2),
  },
  card: {
    marginTop: theme.spacing(2),
    overflow: 'auto',
  },
  message: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  searchField: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  searchResult: {
    marginBottom: theme.spacing(1),
  },
  addResultButton: {
    marginTop: theme.spacing(1),
  },
});

Permissions.propTypes = {
  classes: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  loadCurrentUser: PropTypes.func.isRequired,
};

export default withStyles(styles)(Permissions);
